// PalettePreview.tsx
// Dynamic live preview showing all user-defined colors in their groups

import React from 'react';
import { PaletteStructure } from './types';

interface PalettePreviewProps {
  palette: PaletteStructure;
}

export const PalettePreview: React.FC<PalettePreviewProps> = ({ palette }) => {
  // Calculate luminance to determine if text should be light or dark
  const getLuminance = (hex: string): number => {
    if (!hex || hex.includes('rgba')) return 0.5;
    const rgb = hex.replace('#', '');
    const r = parseInt(rgb.substr(0, 2), 16) / 255;
    const g = parseInt(rgb.substr(2, 2), 16) / 255;
    const b = parseInt(rgb.substr(4, 2), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const getContrastText = (bgColor: string): string => {
    return getLuminance(bgColor) > 0.5 ? '#000000' : '#ffffff';
  };

  // Find a suitable background color (prefer 'background', 'bg', or first color)
  const findBackgroundColor = (): string => {
    const bgKeys = Object.keys(palette.colors).find(key => 
      key.toLowerCase().includes('background') || key.toLowerCase() === 'bg'
    );
    return bgKeys ? palette.colors[bgKeys] : Object.values(palette.colors)[0] || '#ffffff';
  };

  const backgroundColor = findBackgroundColor();

  return (
    <div className="absolute bottom-4 left-4 bg-gray-800 bg-opacity-95 rounded-lg max-w-xs max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
      {/* Buy Me a Coffee Button */}
      <div className="px-3 py-2.5 bg-gray-800 border-b border-gray-700 flex justify-center">
        <a
          href="https://buymeacoffee.com/allaboutstx"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="https://img.buymeacoffee.com/button-api/?text=Buy me a coffee&emoji=☕&slug=allaboutstx&button_colour=FFDD00&font_colour=000000&font_family=Cookie&outline_colour=000000&coffee_colour=ffffff"
            alt="Buy Me A Coffee"
            className="h-9 hover:opacity-90 transition-opacity"
          />
        </a>
      </div>
      
      <div className="overflow-y-auto p-3">
        <div 
          className="space-y-3 p-3 rounded"
          style={{ backgroundColor }}
        >
          {palette.groups.map((group, groupIndex) => (
            <div key={groupIndex} className="space-y-2">
              {/* Group Title */}
              <h5 
                className="text-xs font-bold uppercase tracking-wide"
                style={{ color: getContrastText(backgroundColor) }}
              >
                {group.title}
              </h5>

              {/* Color Grid */}
              <div className="grid grid-cols-2 gap-2">
                {group.colors.map((colorKey) => {
                  const color = palette.colors[colorKey];
                  if (!color) return null;

                  return (
                    <div
                      key={colorKey}
                      className="rounded overflow-hidden border"
                      style={{ 
                        borderColor: getContrastText(backgroundColor) + '20'
                      }}
                    >
                      {/* Color Swatch */}
                      <div
                        className="h-16 flex items-center justify-center relative"
                        style={{ backgroundColor: color }}
                      >
                        {/* Color name overlay */}
                        <div
                          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                          style={{ 
                            backgroundColor: 'rgba(0,0,0,0.7)',
                          }}
                        >
                          <span className="text-xs font-mono text-white">
                            {color}
                          </span>
                        </div>
                      </div>
                      
                      {/* Color Key and Text Sample */}
                      <div
                        className="px-2 py-1.5"
                        style={{ backgroundColor: color }}
                      >
                        <div
                          className="text-xs font-medium truncate"
                          style={{ color: getContrastText(color) }}
                        >
                          {colorKey}
                        </div>
                        <div
                          className="text-xs opacity-75 truncate"
                          style={{ color: getContrastText(color) }}
                        >
                          Sample text
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Color Combinations - show first 3 colors as buttons if group has multiple */}
              {group.colors.length >= 2 && (
                <div className="flex gap-1.5 flex-wrap">
                  {group.colors.slice(0, 3).map((colorKey) => {
                    const color = palette.colors[colorKey];
                    if (!color) return null;

                    return (
                      <button
                        key={colorKey}
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: color,
                          color: getContrastText(color)
                        }}
                      >
                        {colorKey}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Show contrast with background for text colors */}
              {(group.title.toLowerCase().includes('text') || 
                group.colors.some(c => c.toLowerCase().includes('text'))) && (
                <div className="space-y-1">
                  {group.colors.map((colorKey) => {
                    const color = palette.colors[colorKey];
                    if (!color) return null;

                    return (
                      <div
                        key={colorKey}
                        className="px-2 py-1 rounded text-xs"
                        style={{ 
                          backgroundColor,
                          color,
                          border: `1px solid ${color}40`
                        }}
                      >
                        {colorKey}: The quick brown fox jumps
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Summary info */}
          <div
            className="mt-3 pt-3 border-t text-xs text-center"
            style={{ 
              borderColor: getContrastText(backgroundColor) + '20',
              color: getContrastText(backgroundColor) + 'aa'
            }}
          >
            {Object.keys(palette.colors).length} colors in {palette.groups.length} groups
          </div>
        </div>
      </div>
    </div>
  );
};